import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageSquareText, Quote as QuoteIcon } from "lucide-react";
import { api, API } from "../lib/api";
import { useSeo, useSite } from "../lib/site";
import { PageHero, FinalCta, CallButton, TextButton, QuoteButton, SeniorTerms } from "../components/site/Blocks";
import { FaqList } from "./Home";
import { AREA_LINE, TRAVEL_NOTE, CITIES } from "../lib/cities";

const WORK = [
  { id: "gravel-yard", title: "Yard cleanup", text: "Weeds, dry brush and trash cleared from a gravel yard." },
  { id: "room-cleanout", title: "Room cleanout", text: "Furniture and belongings removed, leaving the rooms empty." },
  { id: "refrigerator-haul", title: "Commercial refrigerator removal", text: "A large commercial refrigerator moved out and hauled away by trailer." },
  { id: "cactus-cleanup", title: "Cactus cleanup", text: "Fallen and dead cactus pads cleared and the ground raked clean." },
  { id: "desert-garden", title: "Old pond removal", text: "A worn-out rock pond feature removed and the area left level with gravel." },
];

function WorkPhotos({ limit }) {
  return WORK.slice(0, limit).map((w) => (
    <figure key={w.id} className="overflow-hidden rounded-lg border border-neutral-200 bg-white" data-testid={`work-item-${w.id}`}>
      <div className="flex justify-center bg-neutral-100"><img src={`/work/${w.id}.webp`} alt={`${w.title}: before and after`} className="max-h-[520px] w-full object-contain" loading="lazy" /></div>
      <figcaption className="p-4"><strong className="text-lg">{w.title}</strong><p className="text-base text-neutral-700">{w.text}</p></figcaption>
    </figure>
  ));
}

export function GalleryGrid({ limit }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/public/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  const rest = Math.max((limit || 999) - WORK.length, 0);
  return (
    <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="gallery-grid">
      <WorkPhotos limit={limit || WORK.length} />
      {(items || []).slice(0, rest).map((g) => (
        <figure key={g.id} className="overflow-hidden rounded-lg border border-neutral-200 bg-white" data-testid={`gallery-item-${g.id}`}>
          <div className="grid grid-cols-2">
            {[["Before", g.before_photo_id], ["After", g.after_photo_id]].map(([l, id]) => (
              <div key={l} className="relative aspect-[4/3] bg-neutral-100">
                {id && <img src={`${API}/public/gallery-photo/${id}`} alt={`${g.title} — ${l.toLowerCase()}`} className="h-full w-full object-cover" loading="lazy" />}
                <span className="absolute left-2 top-2 rounded bg-[#171717] px-2 py-1 text-xs font-bold uppercase text-white">{l}</span>
              </div>
            ))}
          </div>
          <figcaption className="p-4"><strong className="text-lg">{g.title}</strong>{g.description && <p className="text-base text-neutral-700">{g.description}</p>}</figcaption>
        </figure>
      ))}
    </div>
  );
}

export function Testimonials() {
  const { site } = useSite();
  return (
    <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="testimonials">
      {(site.testimonials || []).map((t, i) => (
        <blockquote key={i} className="card" data-testid={`testimonial-${i}`}>
          <QuoteIcon className="h-7 w-7 text-[#D9232E]" aria-hidden="true" />
          <p className="mt-2 text-lg leading-relaxed">“{t.quote}”</p>
          <footer className="mt-3 font-bold">— {t.name_display}{t.context && <span className="font-normal text-neutral-600">, {t.context}</span>}</footer>
        </blockquote>
      ))}
    </div>
  );
}

export function About() {
  useSeo("About", "One Punch Services is a locally owned handyman, junk removal and property cleanup business in Albuquerque, New Mexico.");
  const { site } = useSite();
  const b = site.business || {};
  return (
    <div data-testid="about-page">
      <PageHero eyebrow="About us" title="Locally owned. Here to knock it out for you.">One Punch Services helps Albuquerque homeowners, seniors, families, landlords, realtors and property managers get household projects done.</PageHero>
      <section className="wrap grid gap-10 py-16 md:grid-cols-2">
        <div className="space-y-4 text-lg leading-relaxed text-neutral-800">
          {b.about_text ? b.about_text.split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>) : (
            <p>We built One Punch around a few simple ideas: show up when we say we will, explain things clearly, give you pricing upfront in writing, and treat you and your home with respect.</p>
          )}
          {b.credential_note && <p className="font-bold">{b.credential_note}{b.credential_details ? ` — ${b.credential_details}` : ""}</p>}
          {b.insurance_note && <p>{b.insurance_note}</p>}
        </div>
        <div className="rounded-lg bg-[#F5F5F5] p-7">
          <h2 className="text-2xl font-bold uppercase">Our promises</h2>
          <ul className="mt-4 space-y-3 text-lg">
            <li>• Written estimates before any work</li><li>• No work or charges without your approval</li><li>• Respectful, patient communication</li><li>• Clean-up after every job</li>
          </ul>
        </div>
      </section>
      <FinalCta />
    </div>
  );
}

export function BeforeAfter() {
  useSeo("Before & After", "Before-and-after photos of real One Punch Services junk removal, cleanout and handyman projects in Albuquerque.");
  return (
    <div data-testid="before-after-page">
      <PageHero eyebrow="Our work" title="Before & After">Real One Punch jobs, shared with each customer's permission. We never show addresses or personal details.</PageHero>
      <section className="wrap py-12"><GalleryGrid /></section>
      <FinalCta />
    </div>
  );
}

export function Contact() {
  useSeo("Contact", "Call or text One Punch Services — serving Albuquerque, Rio Rancho, Los Lunas, Corrales and Santa Fe, NM — or request a free written estimate online.");
  const { site, phone } = useSite();
  const b = site.business || {};
  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Talk to a real person">Call, text, or send a request online — whatever is easiest for you.</PageHero>
      <section className="wrap grid gap-6 py-16 md:grid-cols-3">
        <div className="card"><Phone className="h-8 w-8 text-[#D9232E]" /><h2 className="mt-3 text-2xl font-bold uppercase">Call</h2><p className="mt-1 text-xl font-bold">{phone}</p><CallButton className="btn-red mt-4 w-full" label="Call now" testid="contact-call-btn" /></div>
        <div className="card"><MessageSquareText className="h-8 w-8 text-[#D9232E]" /><h2 className="mt-3 text-2xl font-bold uppercase">Text photos</h2><p className="mt-1 text-base text-neutral-700">Text a few photos and a short description.</p><TextButton className="btn-dark mt-4 w-full" testid="contact-text-btn" /></div>
        <div className="card"><h2 className="text-2xl font-bold uppercase">Request online</h2><p className="mt-1 text-base text-neutral-700">A short guided form. Photos optional.</p><QuoteButton className="btn-outline mt-4 w-full" testid="contact-quote-btn" /></div>
        <div className="md:col-span-3 space-y-1 text-lg">
          <p data-testid="contact-area-line">{AREA_LINE}</p>
          <p className="flex flex-wrap gap-x-3 text-base">{CITIES.map((c) => <Link key={c.slug} to={`/service-areas/${c.slug}`} className="font-bold underline" data-testid={`contact-city-${c.slug}`}>{c.name}</Link>)}</p>
          {b.email && <p>Email: <a className="break-all underline" href={`mailto:${b.email}`}>{b.email}</a></p>}
          {b.hours && <p>Hours: {b.hours}</p>}
          {b.address && <p>{b.address}</p>}
          <p className="text-base text-neutral-600">Please don't send card numbers, ID documents or door/lock codes by text, email or in the form.</p>
        </div>
      </section>
      <section className="wrap pb-16"><h2 className="h2">Frequently asked questions</h2><FaqList /></section>
    </div>
  );
}

export function Review() {
  useSeo("Leave a Review", "Share an honest review of your experience with One Punch Services.");
  const { site } = useSite();
  const url = site.business?.google_review_url;
  return (
    <div data-testid="review-page">
      <PageHero eyebrow="Thank you" title="How did we do?">We'd appreciate an honest review — good or bad. Reviews are never required for any discount.</PageHero>
      <section className="wrap py-12">{url ? <a href={url} target="_blank" rel="noopener noreferrer" className="btn-red" data-testid="review-google-link">Leave a Google review</a> : <p data-testid="review-not-configured">Our review link isn't set up yet. You can share feedback by calling us.</p>}</section>
    </div>
  );
}

function Legal({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <PageHero eyebrow="Policy" title={title}><p className="rounded-md border-2 border-amber-600 bg-amber-50 p-3 text-base text-amber-900">Draft for owner review. Not legal advice and not a guarantee of legal compliance.</p></PageHero>
      <section className="wrap prose-legal max-w-3xl py-12">{children}</section>
    </div>
  );
}

export function Privacy() {
  useSeo("Privacy Policy", "How One Punch Services collects, uses and protects information you share when requesting an estimate.");
  const { phone } = useSite();
  return (
    <Legal title="Privacy Policy" testid="privacy-page">
      <h2>What we collect</h2><p>When you request an estimate we collect the details you provide: name, phone, optional email, ZIP code and service address, job description, optional photos, preferred timing, whether you requested the senior discount, and how you heard about us. We also record basic website activity, such as button clicks and form starts, to understand how the site is used.</p>
      <h2>What we don't collect</h2><p>We never ask for ID documents, full dates of birth, payment-card numbers or access codes through this website.</p>
      <h2>How we use it</h2><ul><li>To respond to your request, prepare estimates and schedule approved work.</li><li>To send messages related to your request. Marketing messages are only sent if you separately opted in, and you can opt out at any time.</li></ul>
      <h2>Photos</h2><p>Photos you upload are stored privately and are only visible to the business owner. We never publish your photos, address, senior status or estimate without your permission.</p>
      <h2>Sharing</h2><p>We don't sell your information. We use service providers (website hosting, private file storage and email delivery) to operate this site.</p>
      <h2>Retention and deletion</h2><p>We keep closed requests for a limited period and then delete them. You can ask us to delete your information at any time by calling {phone}.</p>
      <h2>Contact</h2><p>Questions? Call or text {phone}.</p>
    </Legal>
  );
}

export function Terms() {
  useSeo("Service and Discount Terms", "Service, estimate and senior discount terms for One Punch Services in Albuquerque, NM.");
  return (
    <Legal title="Service & Discount Terms" testid="terms-page">
      <h2>Estimates</h2><p>Online estimate ranges are estimates only. Final scope and price require review and your written approval before work begins. If the scope changes, we provide a revised estimate for your approval. Written estimates show an expiration date.</p>
      <h2>Scheduling</h2><p>Preferred dates submitted online are requests, not confirmed appointments. An appointment is confirmed only when we confirm it with you directly. Availability can differ between the cities we serve, and we don't promise same-day service.</p>
      <h2>Travel</h2><p>{TRAVEL_NOTE} Any travel charge appears as a separate line on your written estimate before you approve it.</p>
      <h2>Payment</h2><p>Payment terms are shown on each written estimate. We never charge automatically and do not offer subscriptions.</p>
      <h2>Services we don't offer</h2><p>We do not perform electrical, plumbing, gas, HVAC, structural, demolition or other regulated work, and we do not haul hazardous materials.</p>
      <h2 id="senior">Senior discount</h2><SeniorTerms /><p>The senior discount is never tied to leaving a review.</p>
    </Legal>
  );
}

export function NotFound() {
  useSeo("Page not found");
  return <PageHero title="Page not found"><Link to="/" className="btn-red" data-testid="notfound-home-link">Go to the home page</Link></PageHero>;
}
